/**
 * useFinopsPortfolio — hook de dados para o portfólio agregado FinOps.
 *
 * Consome: GET /api/v1/finops/my-portfolio (getPortfolio de api/finopsDashboard.ts).
 *
 * staleTime:
 *   - 60 s  quando o período inclui o mês corrente (janela parcial — dados inacabados)
 *   - 1 h   quando a janela é completamente fechada (todos os meses já fechados)
 *
 * Critério de "parcial": `end` (exclusivo) é posterior ao início do mês corrente,
 * ou seja, o intervalo cobre ao menos um dia do mês em andamento.
 * Como `is_partial` só chega na resposta (e não antes da primeira requisição),
 * usamos o período para decidir o staleTime de forma determinística no momento
 * em que o hook é montado — sem depender do resultado anterior.
 *
 * Chave de cache: ['finops', 'portfolio', start, end]
 */
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { getPortfolio } from '@/api/finopsDashboard'
import type { PortfolioResponse } from '@/api/finopsDashboard'

export interface UseFinopsPortfolioParams {
  start: string   // YYYY-MM-DD inclusivo
  end: string     // YYYY-MM-DD exclusivo (1º dia do mês seguinte ao último mês)
}

/** Retorna true se a janela [start, end) inclui o mês corrente (período parcial). */
function isPeriodPartial(end: string): boolean {
  const curMonthStart = dayjs().startOf('month')
  // end exclusivo > curMonthStart → a janela alcança o mês corrente
  return dayjs(end).isAfter(curMonthStart)
}

const STALE_PARTIAL   = 60_000      // 60 s — dados inacabados do mês corrente
const STALE_CLOSED    = 3_600_000   // 1 h  — todos os meses fechados

export function useFinopsPortfolio({ start, end }: UseFinopsPortfolioParams) {
  const staleTime = isPeriodPartial(end) ? STALE_PARTIAL : STALE_CLOSED

  return useQuery<PortfolioResponse>({
    queryKey: ['finops', 'portfolio', start, end],
    queryFn: () => getPortfolio({ start, end }),
    staleTime,
  })
}
